/**
 * iOS standalone PWAs often report a short window.innerHeight on cold start.
 * Percent/`dvh` shells then end above the home indicator until a tap forces a
 * viewport resize. Pin --shell-height from stable screen metrics in standalone,
 * and keep refreshing as the visual viewport settles.
 */

export type ShellHeightInput = {
  innerHeight: number;
  visualHeight?: number;
  screenWidth: number;
  screenHeight: number;
  standalone: boolean;
  landscape: boolean;
};

/** Pure height pick — exported for tests. */
export const computeShellHeight = ({
  innerHeight,
  visualHeight = 0,
  screenWidth,
  screenHeight,
  standalone,
  landscape,
}: ShellHeightInput) => {
  const measured = Math.max(visualHeight, innerHeight);
  if (!standalone) return Math.round(measured);
  // Fullscreen webview with viewport-fit=cover. Cold-start innerHeight is the
  // short one; screen metrics are stable from the first frame.
  const long = Math.max(screenWidth, screenHeight);
  const short = Math.min(screenWidth, screenHeight);
  return Math.round(Math.max(measured, landscape ? short : long));
};

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches
  || Boolean((navigator as Navigator & { standalone?: boolean }).standalone);

export const shellHeightPx = () => computeShellHeight({
  innerHeight: window.innerHeight,
  visualHeight: window.visualViewport?.height,
  screenWidth: screen.width,
  screenHeight: screen.height,
  standalone: isStandalone(),
  landscape: window.matchMedia("(orientation: landscape)").matches,
});

export const syncShellHeight = () => {
  const next = `${shellHeightPx()}px`;
  if (document.documentElement.style.getPropertyValue("--shell-height") !== next) {
    document.documentElement.style.setProperty("--shell-height", next);
  }
};

/** Call once at boot: sync now, after paint, and on viewport changes. */
export const watchShellHeight = () => {
  syncShellHeight();
  const sync = () => syncShellHeight();
  requestAnimationFrame(() => {
    sync();
    requestAnimationFrame(sync);
  });
  const t1 = window.setTimeout(sync, 50);
  const t2 = window.setTimeout(sync, 300);
  window.addEventListener("resize", sync);
  window.addEventListener("orientationchange", sync);
  window.addEventListener("pageshow", sync);
  document.addEventListener("visibilitychange", sync);
  window.visualViewport?.addEventListener("resize", sync);
  window.visualViewport?.addEventListener("scroll", sync);
  return () => {
    window.clearTimeout(t1);
    window.clearTimeout(t2);
    window.removeEventListener("resize", sync);
    window.removeEventListener("orientationchange", sync);
    window.removeEventListener("pageshow", sync);
    document.removeEventListener("visibilitychange", sync);
    window.visualViewport?.removeEventListener("resize", sync);
    window.visualViewport?.removeEventListener("scroll", sync);
  };
};
